/**
 * "PRESS A KEY" (08 §3-4): a dialog that takes the next keydown, mouse button or turn of the
 * wheel as the new binding. Esc cancels, and so does the CANCEL button; CLEAR empties the slot
 * (the store takes `''` for that). A click on either button is never taken as LMB.
 * A refusal from the bindings store - a conflict, a reserved key - is shown here and the dialog
 * keeps listening, so the player can simply press something else.
 *
 * It listens on `window` in the capture phase and stops the event there: the router must not
 * see an arrow key as "move focus", and nothing else may act on a key that is only being named.
 */

import type { Ctx } from '../context';
import { box, button, el, show } from '../dom';
import { dialog } from '../widgets/confirm';
import { keyName, mouseCode } from './keyNames';

export interface CaptureOptions {
  actionLabel: string;
  slot: number;
  /** The code bound there now, or '' for an empty slot. */
  current: string;
  /** Try the binding: null when it took, otherwise the message to show while still listening. */
  tryBind(code: string): string | null;
  /** The dialog has closed, and this is how. */
  onClose(outcome: CaptureOutcome): void;
}

export type CaptureOutcome = 'bound' | 'cleared' | 'cancelled';

/** How long the release of a captured key or button is watched for. */
const RELEASE_GUARD_MS = 800;
/** A wheel listener on `window` is passive unless told otherwise, and a passive one cannot stop the scroll. */
const WHEEL_OPTIONS = { capture: true, passive: false } as const;

/** Opens the dialog; returns a function that tears its listeners down (for the screen's `dispose`). */
export function openCapture(ctx: Ctx, options: CaptureOptions): () => void {
  let open = true;
  const d = dialog('PRESS A KEY', () => finish('cancelled'), 'menu-capture');
  const message = el('div', 'menu-error menu-hidden');
  message.setAttribute('role', 'alert');
  const was = options.current === '' ? 'NOTHING' : keyName(options.current);
  d.body.append(
    box('menu-capture-action menu-paper', `${options.actionLabel} - SLOT ${options.slot + 1}`),
    box('menu-dim', `BOUND NOW: ${was}`),
    box('menu-prose', 'PRESS THE KEY, MOUSE BUTTON OR WHEEL DIRECTION TO BIND. ESC CANCELS.'),
    message,
  );
  const cancel = button('CANCEL', () => finish('cancelled'));
  d.actions.append(cancel);
  if (options.current !== '') {
    const clear = button('CLEAR THIS SLOT', () => {
      if (attempt('')) finish('cleared');
    });
    d.actions.append(clear);
  }

  const teardown = (): void => {
    open = false;
    window.removeEventListener('keydown', onKeyDown, true);
    window.removeEventListener('mousedown', onMouseDown, true);
    window.removeEventListener('wheel', onWheel, WHEEL_OPTIONS);
  };

  function finish(outcome: CaptureOutcome): void {
    if (!open) return;
    teardown();
    ctx.closeModal();
    options.onClose(outcome);
  }

  function attempt(code: string): boolean {
    const refused = options.tryBind(code);
    if (refused === null) return true;
    message.textContent = `! ${refused}`;
    show(message, true);
    return false;
  }

  /**
   * The press that was just bound is still held. Its release is the browser's to act on - Space
   * clicks the focused button, a side mouse button navigates - so the default of that one
   * release is prevented. It is not stopped: the input layer may still want to hear it.
   */
  function guardRelease(type: 'keyup' | 'mouseup', isIt: (ev: Event) => boolean): void {
    const swallow = (ev: Event): void => {
      if (!isIt(ev)) return;
      ev.preventDefault();
      window.removeEventListener(type, swallow, true);
    };
    window.addEventListener(type, swallow, true);
    window.setTimeout(() => window.removeEventListener(type, swallow, true), RELEASE_GUARD_MS);
  }

  /** The dialog was closed from outside (the menus were hidden): stop listening, take nothing. */
  const gone = (): boolean => {
    if (open && d.modal.el.isConnected) return false;
    teardown();
    return true;
  };

  function onKeyDown(ev: KeyboardEvent): void {
    if (gone()) return;
    ev.preventDefault();
    ev.stopImmediatePropagation();
    // A held Enter - the press that opened this dialog - repeats: that is not a choice of key.
    if (ev.repeat || ev.code === '') return;
    if (ev.code === 'Escape') {
      finish('cancelled');
      return;
    }
    if (!attempt(ev.code)) return;
    // The key is still down. Its key-up must not click whichever button focus returns to.
    const code = ev.code;
    guardRelease('keyup', (up) => (up as KeyboardEvent).code === code);
    finish('bound');
  }

  function onMouseDown(ev: MouseEvent): void {
    if (gone()) return;
    const target = ev.target as Node | null | undefined;
    // The dialog's own buttons stay clickable: CANCEL is never bound as the left mouse button.
    if (target !== null && target !== undefined && d.actions.contains(target)) return;
    ev.preventDefault();
    ev.stopImmediatePropagation();
    if (!attempt(mouseCode(ev.button))) return;
    // The side buttons mean "back" and "forward" to a browser when they come up: not this time.
    const button = ev.button;
    guardRelease('mouseup', (up) => (up as MouseEvent).button === button);
    finish('bound');
  }

  function onWheel(ev: WheelEvent): void {
    if (gone() || ev.deltaY === 0) return;
    ev.preventDefault();
    ev.stopImmediatePropagation();
    if (attempt(ev.deltaY < 0 ? 'WheelUp' : 'WheelDown')) finish('bound');
  }

  window.addEventListener('keydown', onKeyDown, true);
  window.addEventListener('mousedown', onMouseDown, true);
  window.addEventListener('wheel', onWheel, WHEEL_OPTIONS);
  ctx.openModal({ ...d.modal, initialFocus: () => cancel });
  return teardown;
}
